import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBusinessContext } from './BusinessContext';
import { toast } from '@/hooks/ui/use-toast';
import type {
  FinancialAccount,
  FinancialTransaction,
  FinancialDocument,
  Budget,
  BudgetWithItems,
  RecurringTransaction,
  FinancialSummary,
  TransactionFilters,
  DocumentFilters,
  FinancialAccountInput,
  FinancialTransactionInput,
  BudgetInput,
  RecurringTransactionInput,
} from '@/types/financial';

interface FinancialContextType {
  // Data
  accounts: FinancialAccount[];
  transactions: FinancialTransaction[];
  documents: FinancialDocument[];
  budgets: BudgetWithItems[];
  recurringTransactions: RecurringTransaction[];
  summary: FinancialSummary | null;

  // State
  isLoading: boolean;
  error: string | null;
  isEnabled: boolean;

  // Account Actions
  createAccount: (input: FinancialAccountInput) => Promise<FinancialAccount>;
  updateAccount: (id: string, updates: Partial<FinancialAccount>) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  refreshAccounts: () => Promise<void>;

  // Transaction Actions
  createTransaction: (input: FinancialTransactionInput) => Promise<FinancialTransaction>;
  updateTransaction: (id: string, updates: Partial<FinancialTransaction>) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;
  fetchTransactions: (filters?: TransactionFilters) => Promise<FinancialTransaction[]>;
  refreshTransactions: () => Promise<void>;

  // Document Actions
  uploadDocument: (file: File, documentType: string) => Promise<FinancialDocument>;
  verifyDocument: (id: string, extractedData: Record<string, any>) => Promise<void>;
  linkDocumentToTransaction: (documentId: string, transactionId: string) => Promise<void>;
  refreshDocuments: () => Promise<void>;

  // Budget Actions
  createBudget: (input: BudgetInput) => Promise<Budget>;
  updateBudget: (id: string, updates: Partial<Budget>) => Promise<void>;
  deleteBudget: (id: string) => Promise<void>;
  refreshBudgets: () => Promise<void>;

  // Recurring Transaction Actions
  createRecurringTransaction: (input: RecurringTransactionInput) => Promise<RecurringTransaction>;
  updateRecurringTransaction: (id: string, updates: Partial<RecurringTransaction>) => Promise<void>;
  deleteRecurringTransaction: (id: string) => Promise<void>;
  refreshRecurringTransactions: () => Promise<void>;

  // Summary
  refreshSummary: (startDate: string, endDate: string) => Promise<void>;

  // Global refresh
  refreshData: () => Promise<void>;
}

const FinancialContext = createContext<FinancialContextType | null>(null);

export const FinancialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedBusiness: businessDetails } = useBusinessContext();

  // State
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [documents, setDocuments] = useState<FinancialDocument[]>([]);
  const [budgets, setBudgets] = useState<BudgetWithItems[]>([]);
  const [recurringTransactions, setRecurringTransactions] = useState<RecurringTransaction[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard ref to prevent duplicate fetches
  const fetchedForBusinessRef = useRef<string | null>(null);

  // Check if financial is enabled for current business
  const isEnabled = businessDetails?.financial_enabled ?? false;

  // Helper function to call Edge Function (routed by supabase/functions/financial/index.ts).
  // invoke() replaces the function's JSON error with a generic message; surface the real one.
  const callEdgeFunction = async (functionName: string, data?: Record<string, any>): Promise<any> => {
    const { data: result, error } = await supabase.functions.invoke(`financial/${functionName}`, { body: data || {} });

    if (error) {
      const details = await error.context?.json?.().catch(() => null);
      throw new Error(details?.error || `Failed to call ${functionName}`);
    }

    return result;
  };

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    if (!businessDetails?.id || !isEnabled) {
      setAccounts([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('financial_accounts')
        .select('*')
        .eq('business_id', businessDetails.id)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setAccounts(data || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      toast.error('Failed to load accounts', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }, [businessDetails?.id, isEnabled]);

  // Fetch transactions
  const fetchTransactions = useCallback(async (filters?: TransactionFilters) => {
    if (!businessDetails?.id || !isEnabled) {
      setTransactions([]);
      return [];
    }

    try {
      let query = supabase
        .from('financial_transactions')
        .select('*')
        .eq('business_id', businessDetails.id)
        .is('deleted_at', null);

      if (filters?.start_date) {
        query = query.gte('transaction_date', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('transaction_date', filters.end_date);
      }
      if (filters?.type) {
        query = query.eq('type', filters.type);
      }
      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.account_id) {
        query = query.eq('account_id', filters.account_id);
      }
      if (filters?.external_source) {
        query = query.eq('external_source', filters.external_source);
      }
      if (filters?.search_query) {
        query = query.or(`description.ilike.%${filters.search_query}%,reference_number.ilike.%${filters.search_query}%`);
      }

      query = query.order('transaction_date', { ascending: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }
      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTransactions(data || []);
      return data || [];
    } catch (err) {
      console.error('Error fetching transactions:', err);
      toast.error('Failed to load transactions', {
        description: err instanceof Error ? err.message : String(err),
      });
      return [];
    }
  }, [businessDetails?.id, isEnabled]);

  // Fetch documents
  const fetchDocuments = useCallback(async (filters?: DocumentFilters) => {
    if (!businessDetails?.id || !isEnabled) {
      setDocuments([]);
      return;
    }

    try {
      let query = supabase
        .from('financial_documents')
        .select('*')
        .eq('business_id', businessDetails.id)
        .is('deleted_at', null);

      if (filters?.document_type) {
        query = query.eq('document_type', filters.document_type);
      }
      if (filters?.ocr_status) {
        query = query.eq('ocr_status', filters.ocr_status);
      }
      if (filters?.is_verified !== undefined) {
        query = query.eq('is_verified', filters.is_verified);
      }
      if (filters?.start_date) {
        query = query.gte('document_date', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('document_date', filters.end_date);
      }
      if (filters?.search_query) {
        query = query.or(`vendor_name.ilike.%${filters.search_query}%,file_name.ilike.%${filters.search_query}%,invoice_number.ilike.%${filters.search_query}%`);
      }

      query = query.order('created_at', { ascending: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }
      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error('Failed to load documents', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }, [businessDetails?.id, isEnabled]);

  // Fetch budgets with items
  const fetchBudgets = useCallback(async () => {
    if (!businessDetails?.id || !isEnabled) {
      setBudgets([]);
      return;
    }

    try {
      // First fetch budget IDs from the view
      const { data: budgetData, error: budgetError } = await supabase
        .from('budget_performance_view')
        .select('*')
        .eq('business_id', businessDetails.id)
        .order('period_start', { ascending: false });

      if (budgetError) throw budgetError;

      // Then fetch items for each budget
      const budgets = await Promise.all(
        (budgetData || []).map(async (budget: any) => {
          const { data: items } = await supabase
            .from('budget_items')
            .select('*')
            .eq('budget_id', budget.budget_id);

          return {
            ...budget,
            items: items || [],
          };
        })
      );

      setBudgets(budgets);
    } catch (err) {
      console.error('Error fetching budgets:', err);
      toast.error('Failed to load budgets', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }, [businessDetails?.id, isEnabled]);

  // Fetch recurring transactions
  const fetchRecurringTransactions = useCallback(async () => {
    if (!businessDetails?.id || !isEnabled) {
      setRecurringTransactions([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('recurring_transactions')
        .select('*')
        .eq('business_id', businessDetails.id)
        .order('next_occurrence_date', { ascending: true });

      if (error) throw error;
      setRecurringTransactions(data || []);
    } catch (err) {
      console.error('Error fetching recurring transactions:', err);
      toast.error('Failed to load recurring transactions', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }, [businessDetails?.id, isEnabled]);

  // Fetch financial summary
  const fetchSummary = useCallback(async (startDate: string, endDate: string) => {
    if (!businessDetails?.id || !isEnabled) {
      setSummary(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('get_financial_summary', {
          p_business_id: businessDetails.id,
          p_start_date: startDate,
          p_end_date: endDate,
        });

      if (error) throw error;
      setSummary(data && data[0] ? data[0] : null);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, [businessDetails?.id, isEnabled]);

  // Create account
  const createAccount = useCallback(async (input: FinancialAccountInput): Promise<FinancialAccount> => {
    if (!businessDetails?.id) {
      throw new Error('No business selected');
    }

    try {
      const result = await callEdgeFunction('create-account', {
        business_id: businessDetails.id,
        ...input,
      });

      await fetchAccounts();
      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create account';
      toast.error('Failed to create account', {
        description: errorMessage,
      });
      throw err;
    }
  }, [businessDetails?.id, fetchAccounts, callEdgeFunction]);

  // Update account
  const updateAccount = useCallback(async (id: string, updates: Partial<FinancialAccount>) => {
    try {
      const { error } = await supabase
        .from('financial_accounts')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchAccounts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update account';
      toast.error('Failed to update account', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchAccounts]);

  // Delete account
  const deleteAccount = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('financial_accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchAccounts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete account';
      toast.error('Failed to delete account', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchAccounts]);

  // Create transaction
  const createTransaction = useCallback(async (input: FinancialTransactionInput): Promise<FinancialTransaction> => {
    if (!businessDetails?.id) {
      throw new Error('No business selected');
    }

    try {
      const result = await callEdgeFunction('create-transaction', {
        business_id: businessDetails.id,
        ...input,
      });

      await fetchTransactions();
      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create transaction';
      toast.error('Failed to create transaction', {
        description: errorMessage,
      });
      throw err;
    }
  }, [businessDetails?.id, fetchTransactions, callEdgeFunction]);

  // Update transaction
  const updateTransaction = useCallback(async (id: string, updates: Partial<FinancialTransaction>) => {
    try {
      const { error } = await supabase
        .from('financial_transactions')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchTransactions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update transaction';
      toast.error('Failed to update transaction', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchTransactions]);

  // Delete transaction (soft delete)
  const deleteTransaction = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('financial_transactions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await fetchTransactions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete transaction';
      toast.error('Failed to delete transaction', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchTransactions]);

  // Upload document
  const uploadDocument = useCallback(async (file: File, documentType: string): Promise<FinancialDocument> => {
    if (!businessDetails?.id) {
      throw new Error('No business selected');
    }

    try {
      const result = await callEdgeFunction('upload-document', {
        business_id: businessDetails.id,
        file_name: file.name,
        file_type: file.type,
        file_size_bytes: file.size,
        document_type: documentType,
        direction: 'incoming',
      });

      await fetchDocuments();
      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      toast.error('Failed to upload document', {
        description: errorMessage,
      });
      throw err;
    }
  }, [businessDetails?.id, fetchDocuments, callEdgeFunction]);

  // Verify document
  const verifyDocument = useCallback(async (id: string, extractedData: Record<string, any>) => {
    try {
      const { error } = await supabase
        .from('financial_documents')
        .update({
          is_verified: true,
          verified_at: new Date().toISOString(),
          extracted_data: extractedData,
        })
        .eq('id', id);

      if (error) throw error;
      await fetchDocuments();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify document';
      toast.error('Failed to verify document', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchDocuments]);

  // Link document to transaction
  const linkDocumentToTransaction = useCallback(async (documentId: string, transactionId: string) => {
    try {
      const { error } = await supabase
        .from('financial_documents')
        .update({ linked_transaction_id: transactionId })
        .eq('id', documentId);

      if (error) throw error;
      await fetchDocuments();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to link document';
      toast.error('Failed to link document', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchDocuments]);

  // Create budget
  const createBudget = useCallback(async (input: BudgetInput): Promise<Budget> => {
    if (!businessDetails?.id) {
      throw new Error('No business selected');
    }

    try {
      const result = await callEdgeFunction('create-budget', {
        business_id: businessDetails.id,
        ...input,
      });

      await fetchBudgets();
      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create budget';
      toast.error('Failed to create budget', {
        description: errorMessage,
      });
      throw err;
    }
  }, [businessDetails?.id, fetchBudgets, callEdgeFunction]);

  // Update budget
  const updateBudget = useCallback(async (id: string, updates: Partial<Budget>) => {
    try {
      const { error } = await supabase
        .from('budgets')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchBudgets();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update budget';
      toast.error('Failed to update budget', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchBudgets]);

  // Delete budget
  const deleteBudget = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('budgets')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchBudgets();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete budget';
      toast.error('Failed to delete budget', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchBudgets]);

  // Create recurring transaction
  const createRecurringTransaction = useCallback(async (input: RecurringTransactionInput): Promise<RecurringTransaction> => {
    if (!businessDetails?.id) {
      throw new Error('No business selected');
    }

    try {
      const result = await callEdgeFunction('create-recurring-transaction', {
        business_id: businessDetails.id,
        ...input,
      });

      await fetchRecurringTransactions();
      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create recurring transaction';
      toast.error('Failed to create recurring transaction', {
        description: errorMessage,
      });
      throw err;
    }
  }, [businessDetails?.id, fetchRecurringTransactions, callEdgeFunction]);

  // Update recurring transaction
  const updateRecurringTransaction = useCallback(async (id: string, updates: Partial<RecurringTransaction>) => {
    try {
      const { error } = await supabase
        .from('recurring_transactions')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await fetchRecurringTransactions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update recurring transaction';
      toast.error('Failed to update recurring transaction', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchRecurringTransactions]);

  // Delete recurring transaction
  const deleteRecurringTransaction = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('recurring_transactions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchRecurringTransactions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete recurring transaction';
      toast.error('Failed to delete recurring transaction', {
        description: errorMessage,
      });
      throw err;
    }
  }, [fetchRecurringTransactions]);

  // Refresh all data
  const refreshData = useCallback(async () => {
    if (!businessDetails?.id || !isEnabled) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchAccounts(),
        fetchTransactions(),
        fetchDocuments(),
        fetchBudgets(),
        fetchRecurringTransactions(),
      ]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load financial data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [businessDetails?.id, isEnabled, fetchAccounts, fetchTransactions, fetchDocuments, fetchBudgets, fetchRecurringTransactions]);

  // Load data on business change with guard
  useEffect(() => {
    if (!businessDetails?.id || !isEnabled) {
      // Reset state when no business or financial disabled
      setAccounts([]);
      setTransactions([]);
      setDocuments([]);
      setBudgets([]);
      setRecurringTransactions([]);
      setSummary(null);
      fetchedForBusinessRef.current = null;
      return;
    }

    // Skip if already fetched for this business
    if (fetchedForBusinessRef.current === businessDetails.id) {
      return;
    }

    fetchedForBusinessRef.current = businessDetails.id;
    refreshData();
  }, [businessDetails?.id, isEnabled, refreshData]);

  // Memoize context value
  const value = useMemo<FinancialContextType>(() => ({
    // Data
    accounts,
    transactions,
    documents,
    budgets,
    recurringTransactions,
    summary,

    // State
    isLoading,
    error,
    isEnabled,

    // Account Actions
    createAccount,
    updateAccount,
    deleteAccount,
    refreshAccounts: fetchAccounts,

    // Transaction Actions
    createTransaction,
    updateTransaction,
    deleteTransaction,
    fetchTransactions,
    refreshTransactions: fetchTransactions,

    // Document Actions
    uploadDocument,
    verifyDocument,
    linkDocumentToTransaction,
    refreshDocuments: fetchDocuments,

    // Budget Actions
    createBudget,
    updateBudget,
    deleteBudget,
    refreshBudgets: fetchBudgets,

    // Recurring Transaction Actions
    createRecurringTransaction,
    updateRecurringTransaction,
    deleteRecurringTransaction,
    refreshRecurringTransactions: fetchRecurringTransactions,

    // Summary
    refreshSummary: fetchSummary,

    // Global refresh
    refreshData,
  }), [
    accounts,
    transactions,
    documents,
    budgets,
    recurringTransactions,
    summary,
    isLoading,
    error,
    isEnabled,
    createAccount,
    updateAccount,
    deleteAccount,
    fetchAccounts,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    fetchTransactions,
    uploadDocument,
    verifyDocument,
    linkDocumentToTransaction,
    fetchDocuments,
    createBudget,
    updateBudget,
    deleteBudget,
    fetchBudgets,
    createRecurringTransaction,
    updateRecurringTransaction,
    deleteRecurringTransaction,
    fetchRecurringTransactions,
    fetchSummary,
    refreshData,
  ]);

  return (
    <FinancialContext.Provider value={value}>
      {children}
    </FinancialContext.Provider>
  );
};

export const useFinancial = (): FinancialContextType => {
  const context = useContext(FinancialContext);
  if (!context) {
    throw new Error('useFinancial must be used within a FinancialProvider');
  }
  return context;
};
